import grid from '@assets/images/grid.svg';
import log_out from '@assets/images/log_out.svg';
import { TextView } from '@components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../redux/actions';
import { purgePersistedState } from '../../redux';
import { useLogout } from '../../services/mutations';
import * as Styled from './styles';
import { FiMessageCircle } from 'react-icons/fi';
import { ASSETS, PATH } from '../../utils/config';
import {
  AI_WHEEL_GENERATOR_ROUTE_ENABLED,
  CHAT_ENABLED,
  REFERRAL_PROGRAM_SUSPENDED,
} from '../../constants/featureFlags';
import { canAccessReferralDashboard } from '../../constants/referralAccess';
import { FEATURES } from '../../constants/features';
import { useIfUserEligible } from '../../hooks/useIfUserEligible';
import { isUserLoggedIn } from '../../utils/helpers';
import { toggleDock, useDockUnread } from '../ChatDock/dockStore';
import FeedbackModal from '../FeedbackModal';
import NewWheelPopup from '../NewWheelPopup';
import SearchBar from '../SearchBar';
import UserImage from '../UserImage';
import ReferralsIcon from 'assets/images/referrals-icon.svg';
import WaitlistFormModal from '../WaitlistModal';
import { resetGallery } from '../../redux/reducers/gallery';
import { clearGalleryPopup } from '../../redux/reducers/galleryPopup';
import { getLoggedInUser } from '../../utils/helpers';
import { EnterBetaButton } from '../EnterBetaButton';

const Navbar = ({ onYugaLogoClick }) => {
  const { userProfile } = useSelector((state) => state.userProfile);

  const [openWheelPopup, setOpenWheelPopup] = useState(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const [isLoggingOut, setisLoggingOut] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [openWaitlistModal, setOpenWaitlistModal] = useState(false);

  const userType = userProfile?.referral_type;
  // Enrollment in the (suspended) Referral / Earnings Program. `explorePage: true`
  // is the hook's existing "only query once the viewer is logged in" switch (same
  // use as ViewWheel) — the dropdown this feeds only renders for a logged-in user,
  // so an anonymous visitor makes no eligibility call. Same react-query key as the
  // /referrals page, so the answer is shared from cache, not fetched twice.
  const { isInReferralProgram } = useIfUserEligible(FEATURES.REFFERAL_PROGRAM, {
    explorePage: true,
  });
  // GR-2: unread badge for the header chat-indicator (published by MiniDmDock).
  const chatUnread = useDockUnread();

  let nav = useNavigate();
  const dispatch = useDispatch();
  let userLogout = useLogout();
  const dropdownRef = useRef();

  const handleCloseWheelPopup = () => {
    setOpenWheelPopup(false);
  };

  const handleLogout = () => {
    setisLoggingOut(true);
    userLogout.mutate(null, {
      onSuccess: (response) => {
        setisLoggingOut(false);
        dispatch(logout());
        dispatch(resetGallery());
        dispatch(clearGalleryPopup());
        localStorage.removeItem('keyCloak');
        sessionStorage.removeItem("referralData");
        purgePersistedState(); // EW-1004: clear persist:root remnant
        nav('/login');
      },
      onError: (err) => {
        setisLoggingOut(false);
      },
    });
  };

  const onClickLogo = () => {
    if (isUserLoggedIn()) nav(`/gallery/${getLoggedInUser().id}`);
    else nav('/login');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setClicked(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    // G3/G4 (coverage doc, LIVE-measured nav=0 / banner=0 on every surface):
    // the persistent app chrome had no landmark structure at all. `<header>`
    // at document top-level (not nested in main/article/aside) carries the
    // implicit `role="banner"` landmark; the `<nav>` wrapping the actual
    // navigational controls (home logo, search, chat, account menu incl. its
    // Gallery/Settings/Logout links) gives SR users a "navigation" region to
    // jump to. `Container` is `display:grid` with exactly 3 track children
    // (Left/Center/Right) — the nav wrapper uses `display:contents`
    // (Styled.NavRegion) so it does NOT become a 4th grid box and collapse
    // the layout; its children keep participating in the grid exactly as
    // before. Purely additive landmark semantics, zero visual change.
    <Styled.Container as="header">
      <Styled.NavRegion aria-label="Primary">
        <Styled.LeftContainer>
          <Styled.Logo
            onClick={() => (onYugaLogoClick ? onYugaLogoClick() : onClickLogo())}
            src={ASSETS.iyugaLogo}
          ></Styled.Logo>
          <Styled.SearchBar>
            <SearchBar />
          </Styled.SearchBar>
        </Styled.LeftContainer>
        <Styled.CenterContainer>
          {/* <Styled.CreateWheelContainer onClick={() => setOpenWheelPopup(true)}>
            <Styled.CreateWheelImage src={ASSETS.createWheelNav} />
          </Styled.CreateWheelContainer> */}
        </Styled.CenterContainer>
        <Styled.RightContainer>
        {/* {isUserLoggedIn() ? (
          <Styled.InviteContainer onClick={() => nav("/invite")}>
            Invite Friends
          </Styled.InviteContainer>
        ) : null} */}

        {!!sessionStorage.getItem('referralData') && (
          <EnterBetaButton onClickBeta={() => setOpenWaitlistModal(true)} />

          // <Styled.Beta
          //   src={ASSETS.beta}
          //   onClick={() => setOpenWaitlistModal(true)}
          // />
        )}

        {/* GR-2: DM entry point — Facebook-style header chat-indicator. Gated by
            CHAT_ENABLED (byte-identical to today while OFF) + logged-in. Clicking
            opens the bottom-right dock; the docked chat box then persists for the
            active conversation. */}
        {CHAT_ENABLED && isUserLoggedIn() && (
          <Styled.ChatIndicatorBtn
            type="button"
            title="Messages"
            aria-label={`Messages${chatUnread ? `, ${chatUnread} unread` : ''}`}
            aria-haspopup="dialog"
            onClick={(e) => toggleDock(e.currentTarget)}
          >
            <FiMessageCircle aria-hidden="true" />
            {chatUnread && <Styled.ChatBadge aria-hidden="true">{chatUnread}</Styled.ChatBadge>}
          </Styled.ChatIndicatorBtn>
        )}
        <Styled.UserContainer
          clicked={clicked}
          ref={dropdownRef}
          onClick={() => {
            setClicked(true);
            isUserLoggedIn() && setShowDropdown(!showDropdown);
          }}
        >
          {isUserLoggedIn() ? (
            <>
              <UserImage style={{ marginRight: 10 }} image={userProfile?.profile_image} size={42} />
              {isLoggingOut ? (
                <Styled.LoggingOut variant="subtitle">Logging Out...</Styled.LoggingOut>
              ) : (
                <>
                  <Styled.UserName variant="subtitle">
                    {userProfile?.first_name} {userProfile?.last_name}
                  </Styled.UserName>
                  <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                </>
              )}
            </>
          ) : (
            <Styled.ButtonView>
              <Styled.Button onClick={() => nav('/login')} style={{ marginRight: 12 }}>
                Log In
              </Styled.Button>
              <Styled.Button onClick={() => nav('/signup')}>Sign Up</Styled.Button>
            </Styled.ButtonView>
          )}

          {showDropdown && (
            <Styled.Dropdown>
              <Styled.DropdownItem onClick={() => nav(`/gallery/${getLoggedInUser().id}`)}>
                <Styled.DropdownImage src={grid} alt="Gallery" /> Gallery
              </Styled.DropdownItem>
              {/* Referral / Earnings Program is SUSPENDED for NEW sign-ups — the
                  nav entry is hidden by the shared REFERRAL_PROGRAM_SUSPENDED
                  switch, not removed. A member who was ALREADY enrolled keeps it
                  (Gordon, 2026-09-03: enrolled members keep access to their
                  Referral Program page while the program is suspended), which is
                  what `canAccessReferralDashboard` adds — a non-member's
                  `isInReferralProgram` is false, so nothing changes for them. The
                  entry returns for everyone when REACT_APP_REFERRAL_PROGRAM=true. */}
              {canAccessReferralDashboard({
                suspended: REFERRAL_PROGRAM_SUSPENDED,
                isInReferralProgram,
              }) && userType !== 'Default' && (
                <Styled.DropdownItem onClick={() => nav('/referrals')}>
                  <Styled.DropdownImage src={ReferralsIcon} alt="Referrals" /> Referrals
                </Styled.DropdownItem>
              )}

              {/* AI-03: entry point for the (previously dark) AI Wheel
                  Generator. Flag-gated (default OFF) so the item is not rendered
                  and the Navbar is byte-identical to today until an environment
                  opts in — pairs with the flag-gated /create/ai-wheel route. */}
              {AI_WHEEL_GENERATOR_ROUTE_ENABLED && (
                <Styled.DropdownItem
                  onClick={() => nav(PATH.AI_WHEEL_GENERATOR)}
                >
                  <Styled.DropdownImage src={ASSETS.NewAiIcon} alt="Create with AI" />
                  Create with AI
                </Styled.DropdownItem>
              )}

              <Styled.DropdownItem onClick={() => nav('/settings')}>
                <Styled.DropdownImage src={ASSETS.settings} alt="Settings" />
                Settings
              </Styled.DropdownItem>
              <Styled.DropdownItem onClick={handleLogout}>
                <Styled.DropdownImage src={log_out} alt="Logout" /> Log Out
              </Styled.DropdownItem>
            </Styled.Dropdown>
          )}
        </Styled.UserContainer>
        </Styled.RightContainer>
      </Styled.NavRegion>
      <NewWheelPopup
        openWheelPopup={openWheelPopup}
        handleCloseWheelPopup={handleCloseWheelPopup}
      />
      <FeedbackModal
        openFeedbackModal={openFeedbackModal}
        setOpenFeedbackModal={setOpenFeedbackModal}
      />
      <WaitlistFormModal open={openWaitlistModal} onClose={() => setOpenWaitlistModal(false)} />
    </Styled.Container>
  );
};

export default Navbar;
